/**
 * Partner Preview Phase 1 — shared partner-mode orchestration.
 *
 * Loaded by BOTH breed-collection systems (the Memorial Builder's breed
 * mode and Commerce V2's collection mode) so the actual "save a draft,
 * show the confirmation modal, redirect on success" logic exists exactly
 * ONCE — never duplicated per system. Each system's own existing
 * eligibility/resolver/validation logic is untouched; this file only ever
 * runs AFTER that existing logic has already validated the customer's
 * current selection, and only ever replaces the final action.
 *
 * Liquid cannot read the `?partner=` query string at all (a firm Shopify
 * platform limitation — arbitrary query parameters are not exposed to
 * Liquid), so partner identity is resolved ENTIRELY client-side: this
 * script reads window.location.search and asks the backend (via the
 * existing Shopify App Proxy, /apps/memorial/partner/context) whether
 * that partner key is real and active. The raw query-string value is
 * NEVER trusted for display name, return URL, or any authorization
 * decision — it is only ever used as a lookup key, re-validated by the
 * backend on every call (both the context check AND the draft save
 * independently re-resolve it; no earlier client-side "valid" result is
 * ever trusted by itself for the actual save).
 *
 * Fail-closed by construction: if no `?partner=` value is present, or
 * resolution has not completed yet, or resolution comes back invalid,
 * every function below is a no-op and the calling button/form behaves
 * exactly as it already did for a normal BPF customer — this file never
 * downgrades/disables anything, it only ever additively upgrades a
 * button's behavior once a partner context is positively confirmed.
 *
 * PHASE 2A ADDITION — popup/window partner experience. The cross-origin
 * iframe path specified in the original Phase 2 draft was found to be
 * BLOCKED (live-confirmed: bestpetsforever.com sends both
 * `X-Frame-Options: DENY` and `Content-Security-Policy: frame-ancestors
 * 'none'`, a Shopify/Cloudflare platform-enforced, non-theme-configurable
 * policy). The approved replacement opens this whole experience in a
 * BPF-owned POPUP/NEW WINDOW (assets/bpf-partner-widget.js, running on
 * the PARTNER's page, calls window.open()) instead of an <iframe> — nothing
 * in this file ever runs inside a frame. Three additions support that:
 *
 *   1. `mode=partner-window` — a second, generalized URL signal (alongside
 *      `partner=`) that this page is being viewed INSIDE that BPF popup,
 *      never trusted alone (see initPartnerWindowPresentation()).
 *   2. `parent_origin` — the opener's own origin, supplied by the widget
 *      when it opens the popup (it knows its own window.location.origin;
 *      BPF cannot and must not guess it). Two independent layers use it:
 *      (a) the backend (resolvePartnerWindowContext(), InsightOS'
 *      partner-registry.ts) re-validates it, on every resolveContext()
 *      call in window mode, against that specific partner's own
 *      approved_embed_origins allowlist — an unapproved or MISSING
 *      origin makes ctx.valid false, which blocks suppression/chrome AND
 *      the entire handleReturnToPartner()/postToOpener() path upstream,
 *      before any message is ever sent; (b) postToOpener() then uses the
 *      SAME value as the postMessage targetOrigin — the browser itself
 *      also independently verifies the opener's actual origin matches
 *      before delivering, and postToOpener() never falls back to a
 *      wildcard, so a missing/wrong value fails closed at both layers,
 *      not just one.
 *   3. rewriteLinks() now propagates the full PROPAGATED_PARAMS set
 *      (partner/mode/parent_origin), not just partner — so all three
 *      survive real top-level navigation from the BPF landing page
 *      through the breed picker into breed-collection-v2/Commerce V2,
 *      exactly the way `partner` alone already did in Phase 1.
 */
(function () {
  'use strict';

  var PARTNER_CONTEXT_URL = '/apps/memorial/partner/context';
  var PARTNER_DRAFT_URL = '/apps/memorial/partner/draft';
  var PARTNER_WINDOW_MESSAGE_VERSION = 1;
  var PROPAGATED_PARAMS = ['partner', 'mode', 'parent_origin', 'partner_session_id'];
  // Phase 2B.2A — same public telemetry ingestion endpoint assets/bpf-
  // partner-widget.js posts to. A plain constant here too (this file
  // runs on bestpetsforever.com, a DIFFERENT origin from the Cloud Run
  // API host, so it's still a cross-origin call, not a same-origin path).
  var BPF_TELEMETRY_URL = 'https://bpf-memorial-api-10424258225.us-central1.run.app/partner/telemetry/v1';

  // --- Operational telemetry sender — see assets/bpf-partner-widget.js's
  // OWN copy of this exact function for the full rationale (fire-and-
  // forget, fixed allowlist, never throws). Duplicated rather than
  // shared, matching this theme's established per-file self-containment
  // convention for every partner-mode-adjacent asset. ---
  function sendPartnerTelemetry(eventName, payload) {
    try {
      if (typeof fetch !== 'function') return;
      var body = {
        event_name: eventName,
        partner_id: payload.partnerId,
        source: 'popup_client',
      };
      if (payload.partnerSessionId) body.partner_session_id = payload.partnerSessionId;
      if (payload.stage) body.stage = payload.stage;
      if (payload.errorCode) body.error_code = payload.errorCode;
      if (payload.component) body.component = payload.component;
      if (payload.animal) body.animal = payload.animal;
      if (payload.breed) body.breed = payload.breed;
      if (payload.memorialType) body.memorial_type = payload.memorialType;
      fetch(BPF_TELEMETRY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(body),
        keepalive: true,
        mode: 'cors',
      }).catch(function () {
        /* analytics must never surface an error to the customer */
      });
    } catch (e) {
      /* ignored — see above */
    }
  }

  var rawPartnerKey = null;
  try {
    var params = new URLSearchParams(window.location.search);
    var fromQuery = params.get('partner');
    if (fromQuery && fromQuery.trim() !== '') {
      rawPartnerKey = fromQuery.trim();
    }
  } catch (e) {
    rawPartnerKey = null;
  }

  var rawMode = null;
  var rawParentOrigin = null;
  var rawPartnerSessionId = null;
  try {
    var winParams = new URLSearchParams(window.location.search);
    var modeVal = winParams.get('mode');
    if (modeVal && modeVal.trim() !== '') rawMode = modeVal.trim();
    var parentOriginVal = winParams.get('parent_origin');
    if (parentOriginVal && parentOriginVal.trim() !== '') rawParentOrigin = parentOriginVal.trim();
    // Phase 2B.2A — analytics correlation only, format-checked (never
    // trusted for anything security-sensitive, exactly like every other
    // raw query-string value in this file). A missing/malformed value
    // simply means telemetry/draft correlation is unavailable for this
    // one journey — never a functional failure.
    var partnerSessionIdVal = winParams.get('partner_session_id');
    if (partnerSessionIdVal && /^ps_[0-9a-f]{32}$/.test(partnerSessionIdVal)) rawPartnerSessionId = partnerSessionIdVal;
  } catch (e) {
    rawMode = null;
    rawParentOrigin = null;
    rawPartnerSessionId = null;
  }

  function getMode() {
    return rawMode;
  }

  function isPartnerWindowMode() {
    return rawMode === 'partner-window';
  }

  var resolvedContext = null; // { valid, partnerId, displayName } once settled
  var contextPromise = null;

  function isDebugEnabled() {
    return typeof window !== 'undefined' && window.__BPF_PARTNER_DEBUG__ === true;
  }

  function debugLog() {
    if (!isDebugEnabled()) return;
    var args = ['[bpf-partner-debug]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  /**
   * Partner analytics — reuses the SAME established, existing-in-this-
   * theme convention as assets/bpf-memorial-designer.js's own
   * dispatchBuilderEvent(): an ordinary `window.gtag('event', ...)` call
   * against whatever ambient GA4 tag this storefront already has loaded
   * (typically injected store-wide by Shopify's own "Google & YouTube"
   * sales channel / a Web Pixel extension, entirely outside theme
   * source) — never a second analytics library, never a hardcoded
   * Measurement ID, never bypassing whatever consent state gtag.js
   * itself is already enforcing. Fire-and-forget and defensive by
   * construction: analytics must never block or break the partner flow.
   *
   * Payload is restricted to partner_id/partner_session_id/animal/
   * breed/memorial_type/stage only — this function has no parameter for
   * anything else (no partner_draft_id, no customer email, no pet name,
   * no photo/file id, no story text, no address) so there is no way for
   * a future call site to accidentally widen the payload without
   * editing this allowlist-shaped signature itself. partner_session_id
   * is non-PII by construction (see assets/bpf-partner-widget.js's
   * generatePartnerSessionId()) — safe for GA4 exactly like partner_id.
   */
  function dispatchPartnerEvent(eventName, payload) {
    try {
      if (typeof window.gtag !== 'function') return false;
      var allowed = ['partner_id', 'partner_session_id', 'animal', 'breed', 'memorial_type', 'stage'];
      var cleanPayload = {};
      allowed.forEach(function (key) {
        var value = payload ? payload[key] : undefined;
        if (value !== null && value !== undefined && value !== '') cleanPayload[key] = value;
      });
      window.gtag('event', eventName, cleanPayload);
      debugLog('analytics event dispatched', { eventName: eventName, payload: cleanPayload });
      return true;
    } catch (err) {
      // Analytics must never block or break the partner flow.
      return false;
    }
  }

  function resolveContext() {
    if (!rawPartnerKey) {
      return Promise.resolve({ valid: false });
    }
    if (contextPromise) return contextPromise;

    // Phase 2A: a genuine partner-window page ALWAYS sends windowMode:true
    // once mode=partner-window is present — even if rawParentOrigin
    // itself is missing (e.g. a forged/incomplete URL that sets
    // mode=partner-window but omits parent_origin). This is deliberate:
    // the backend's own gate is "windowMode ? stricter origin-checked
    // resolution : plain identity resolution" (never "origin present ?
    // ... : ..."), so simply OMITTING parent_origin can never silently
    // downgrade a window-mode request back to identity-only resolution —
    // resolvePartnerWindowContext() (src/server/partner-registry.ts)
    // itself fails closed on an empty/missing origin. The legacy bare
    // `?partner=` path never sends windowMode at all, preserving
    // identity-only resolution exactly as before for every other caller.
    var requestBody = { partner: rawPartnerKey };
    if (isPartnerWindowMode()) {
      requestBody.windowMode = true;
      requestBody.origin = rawParentOrigin || '';
    }

    contextPromise = fetch(PARTNER_CONTEXT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    })
      .then(function (response) {
        if (!response.ok) {
          sendPartnerTelemetry('partner_context_error', { partnerId: rawPartnerKey, partnerSessionId: rawPartnerSessionId, errorCode: 'context_request_failed', stage: 'context_resolve', component: 'http_status_' + response.status });
          return { valid: false };
        }
        return response.json();
      })
      .then(function (data) {
        var result = data && data.valid === true && data.partnerId && data.displayName
          ? { valid: true, partnerId: data.partnerId, displayName: data.displayName }
          : { valid: false };
        resolvedContext = result;
        debugLog('context resolved', result);
        return result;
      })
      .catch(function (err) {
        var result = { valid: false };
        resolvedContext = result;
        debugLog('context resolution failed', { message: err && err.message });
        sendPartnerTelemetry('partner_context_error', { partnerId: rawPartnerKey, partnerSessionId: rawPartnerSessionId, errorCode: 'context_request_failed', stage: 'context_resolve' });
        return result;
      });

    return contextPromise;
  }

  function isActive() {
    return Boolean(resolvedContext && resolvedContext.valid);
  }

  function getDisplayName() {
    return isActive() ? resolvedContext.displayName : null;
  }

  function getPartnerId() {
    return isActive() ? resolvedContext.partnerId : null;
  }

  // --- Modal orchestration (shared by both integration paths) ---

  function getModal() {
    return document.querySelector('[data-partner-return-modal]');
  }

  function populateModalName(modal, displayName) {
    var nameEls = modal.querySelectorAll('[data-partner-return-modal-name]');
    nameEls.forEach(function (el) {
      el.textContent = displayName;
    });
  }

  function setModalError(modal, message) {
    var errorEl = modal.querySelector('[data-partner-return-modal-error]');
    if (!errorEl) return;
    if (!message) {
      errorEl.hidden = true;
      errorEl.textContent = '';
      return;
    }
    errorEl.hidden = false;
    errorEl.textContent = message;
  }

  /**
   * Phase 2A.2 fix: `aria-disabled` alone is an ARIA-only signal — it
   * does NOT stop a native <button>'s click event from firing, so a real
   * double-click during an in-flight save could previously fire a SECOND
   * concurrent draft-save request (confirmed possible: the backend has
   * no idempotency key and creates a genuinely new draft on every call).
   * Now also sets the real `disabled` DOM property, which does block
   * clicks, alongside `aria-disabled` for the same assistive-tech
   * semantics as before. Also swaps the visible label between the normal
   * confirm text and "Saving Memorial…" (see snippets/bpf-partner-
   * return-modal.liquid) so the button's own text — not just a spinner
   * — communicates the in-flight state.
   */
  function setModalBusy(modal, isBusy) {
    var confirmBtn = modal.querySelector('[data-partner-return-modal-confirm]');
    var keepEditingBtn = modal.querySelector('[data-partner-return-modal-keep-editing]');
    var spinner = modal.querySelector('[data-partner-return-modal-spinner]');
    var confirmLabel = modal.querySelector('[data-partner-return-modal-confirm-label]');
    var savingLabel = modal.querySelector('[data-partner-return-modal-saving-label]');
    if (confirmBtn) {
      confirmBtn.disabled = isBusy;
      confirmBtn.setAttribute('aria-disabled', isBusy ? 'true' : 'false');
    }
    if (keepEditingBtn) keepEditingBtn.disabled = isBusy;
    if (spinner) spinner.hidden = !isBusy;
    if (confirmLabel) confirmLabel.hidden = isBusy;
    if (savingLabel) savingLabel.hidden = !isBusy;
  }

  /**
   * Opens the shared confirmation modal for a specific "Return to
   * Partner" attempt, wiring fresh listeners for THIS attempt only
   * (the confirm button is cloned/replaced first so no listener from a
   * previous open — e.g. a Vault panel's attempt followed by a Capsule
   * panel's attempt on the same collection page — can ever double-fire).
   *
   * `payload` — the fully-collected, already-validated current
   * selection (product/variant/animal/breed/properties) from the
   * calling system's OWN existing resolver/collection logic. This
   * function never re-derives or second-guesses that data.
   */
  function handleReturnToPartner(payload, triggerEl) {
    if (!isActive()) return; // fail-closed: never opens without a confirmed partner context
    var modal = getModal();
    if (!modal || typeof modal.show !== 'function') return;

    dispatchPartnerEvent('partner_return_clicked', {
      partner_id: getPartnerId(),
      animal: payload && payload.animal,
      breed: payload && payload.breed,
      memorial_type: payload && payload.memorialType,
    });

    var displayName = getDisplayName();
    populateModalName(modal, displayName);
    setModalError(modal, null);
    setModalBusy(modal, false);

    var confirmBtnOld = modal.querySelector('[data-partner-return-modal-confirm]');
    var keepEditingBtnOld = modal.querySelector('[data-partner-return-modal-keep-editing]');
    var confirmBtn = confirmBtnOld.cloneNode(true);
    var keepEditingBtn = keepEditingBtnOld.cloneNode(true);
    confirmBtnOld.parentNode.replaceChild(confirmBtn, confirmBtnOld);
    keepEditingBtnOld.parentNode.replaceChild(keepEditingBtn, keepEditingBtnOld);

    keepEditingBtn.addEventListener('click', function () {
      modal.hide();
    });

    // Phase 2A.2 fix: defense-in-depth against a duplicate concurrent
    // save, alongside setModalBusy()'s real `disabled` DOM property
    // (added this same pass) — a closure-scoped flag survives even if
    // some browser/assistive-tech path ever lets a click through a
    // disabled button. Fresh per handleReturnToPartner() call (a new
    // confirmBtn is cloned every open), so it can never leak stale state
    // from a previous open into this one.
    var saveInFlight = false;

    confirmBtn.addEventListener('click', function () {
      if (saveInFlight) return; // real double-click guard, not just visual
      saveInFlight = true;

      setModalError(modal, null);
      setModalBusy(modal, true);
      debugLog('draft save starting', { partnerId: getPartnerId(), payload: payload });

      // Phase 2A.1: mirrors the SAME windowMode/origin signal resolveContext()
      // already sends — required here too, since the backend's draft-save
      // route (src/server/production-app.ts's handlePartnerDraft) now
      // branches on it: a window-mode save resolves via
      // resolvePartnerWindowContext() (partner + approved origin, no
      // return URL involved) instead of the legacy resolvePartnerDestination()
      // (which hard-requires approved_return_url — never populated/relevant
      // for a save that\'s never going to redirect anywhere).
      var draftRequestBody = { partner: rawPartnerKey, draft: payload };
      if (isPartnerWindowMode()) {
        draftRequestBody.windowMode = true;
        draftRequestBody.origin = rawParentOrigin || '';
      }
      // Phase 2B.2A — correlation-only (see partner-draft.ts's own
      // header comment); the server format-validates and silently drops
      // it if malformed, exactly like every other optional field here.
      if (rawPartnerSessionId) draftRequestBody.partnerSessionId = rawPartnerSessionId;

      sendPartnerTelemetry('partner_draft_save_started', { partnerId: rawPartnerKey, partnerSessionId: rawPartnerSessionId, stage: 'draft_save' });

      fetch(PARTNER_DRAFT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draftRequestBody),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          // Phase 2A.2 ROOT-CAUSE FIX: this used to also require
          // `result.data.returnUrl` truthy for EVERY path — but a
          // window-mode save legitimately, by design, returns
          // `returnUrl: null` (the popup never redirects anywhere; see
          // the isPartnerWindowMode() branch below). That meant every
          // genuinely successful window-mode save was thrown away here
          // and shown to the customer as a failure — confirmed via real
          // Cloud Run logs showing drafts WERE created server-side for
          // the user's actual clicks, while the UI still showed the
          // generic error. `returnUrl` is now only required on the
          // LEGACY (non-window-mode) path, which is the only path that
          // actually uses it.
          var success = result.ok && result.data && result.data.ok === true && result.data.partnerDraftId && (isPartnerWindowMode() || result.data.returnUrl);
          if (!success) {
            // Phase 2B.2A — distinguishes a genuine non-2xx HTTP failure
            // from an HTTP-ok response whose BODY was logically invalid
            // (missing fields, ok:false, etc.) — the outer .catch()
            // below reads this back to pick the right error_code,
            // without needing to duplicate this logic there.
            var invalidError = new Error('draft save did not succeed');
            invalidError.bpfErrorCode = result.ok ? 'draft_save_invalid_response' : 'draft_save_http_failure';
            throw invalidError;
          }
          debugLog('draft saved', { partnerDraftId: result.data.partnerDraftId });
          dispatchPartnerEvent('partner_draft_saved', {
            partner_id: getPartnerId(),
            partner_session_id: rawPartnerSessionId,
            animal: payload && payload.animal,
            breed: payload && payload.breed,
            memorial_type: payload && payload.memorialType,
          });

          if (isPartnerWindowMode()) {
            // Phase 2A path: this window is a BPF-owned popup opened by
            // assets/bpf-partner-widget.js on the partner's own page.
            // Never redirect the popup anywhere — notify the opener via
            // postMessage and let the WIDGET (not BPF) own what happens
            // next on the partner's site (attaching the draft to their
            // cart is explicitly Phase 2B, not this window's job).
            saveInFlight = false;
            setModalBusy(modal, false);
            modal.hide();
            notifyOpenerDraftSaved(result.data.partnerDraftId);
            return;
          }

          // Legacy/direct-link path (no mode=partner-window — e.g. a
          // bookmarked or directly-shared breed-collection-v2 URL that
          // still only carries `?partner=`): preserved exactly as
          // Phase 1 shipped it. Only ever redirects after a confirmed
          // successful save that itself returned the approved return URL.
          window.location.href = result.data.returnUrl;
        })
        .catch(function (err) {
          debugLog('draft save failed', { message: err && err.message });
          var errorCode = (err && err.bpfErrorCode) || 'draft_save_http_failure'; // a real thrown fetch()-level error (network down, etc.) never sets bpfErrorCode — defaults to the same code a non-2xx response gets, since both are genuine transport-level failures
          sendPartnerTelemetry('partner_draft_save_error', { partnerId: rawPartnerKey, partnerSessionId: rawPartnerSessionId, errorCode: errorCode, stage: 'draft_save' });
          saveInFlight = false; // allow retry — a failed save must never permanently lock the button
          setModalBusy(modal, false);
          setModalError(
            modal,
            "We couldn't save your selections just now. Please try again — your memorial is still exactly as you left it."
          );
        });
    });

    modal.show(triggerEl);
  }

  // --- Integration path 1: Commerce V2 (direct call from an existing submit handler) ---
  // No wiring needed here beyond exposing handleReturnToPartner/isActive —
  // the calling section's own submit handler decides when to call it.

  // --- Integration path 2: Memorial Builder (generic <form> submit interception) ---

  /**
   * Attaches a capture-phase submit interceptor for an existing add-to-
   * cart <form>.
   *
   * ROOT-CAUSE FIX (pre-live audit): the original version of this
   * function attached its capture-phase listener directly TO formEl —
   * the same node the stock <product-form> component (assets/product-
   * form.js) also attaches its own (non-capture) submit listener to.
   * Per the DOM event-dispatch algorithm, when two listeners are
   * registered on the SAME target node, they fire in REGISTRATION
   * ORDER regardless of their capture flag — the capture flag only
   * changes ordering relative to listeners on ANCESTOR/DESCENDANT
   * nodes, never relative to other listeners on the identical node.
   * <product-form>'s listener registers at custom-element-upgrade time
   * (as soon as the tag is parsed), which is BEFORE this deferred
   * script runs — so it was actually running FIRST despite the
   * `{capture: true}` flag here, already dispatching a real /cart/add
   * fetch before this interceptor ever got a chance to act. Confirmed
   * via a real DOM-event-ordering test, not source-text inspection
   * alone (see shadow-mode-partner-submit-interception.js).
   *
   * FIX: attach to `document` (a genuine ANCESTOR of every form on the
   * page) instead of to formEl itself, filtered to this exact form via
   * `event.target === formEl`. Capture-phase listeners on an ancestor
   * ALWAYS run during the capture-phase tree-descent, which completes
   * BEFORE the event ever reaches the target node's own listener list
   * (AT_TARGET phase) — this ordering is guaranteed by the dispatch
   * algorithm regardless of any other listener's registration time,
   * so it needs no knowledge of <product-form>'s own internals at all.
   *
   * When partner mode is not active, this remains a complete no-op and
   * the form submits exactly as it always has.
   *
   * `getPayload(formEl)` — caller-supplied, reads whatever that specific
   * form's own fields mean (kept in the calling Liquid/JS, never
   * duplicated here) and returns the draft payload object.
   */
  function attachToForm(formEl, getPayload) {
    if (!formEl) return;
    document.addEventListener(
      'submit',
      function (event) {
        if (event.target !== formEl) return; // scoped to this exact form only — never intercepts an unrelated form on the same page
        if (!isActive()) return; // not partner mode (or not yet resolved) — real submit proceeds untouched
        event.preventDefault();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
        var payload = typeof getPayload === 'function' ? getPayload(formEl) : {};
        handleReturnToPartner(payload, formEl.querySelector('[type="submit"]'));
      },
      { capture: true }
    );
  }

  /**
   * Swaps an existing Add to Cart button's label once (and only once)
   * partner context resolves as valid. Never touches the button before
   * resolution completes, and never touches it at all if resolution
   * comes back invalid — the button keeps behaving as a completely
   * normal Add to Cart control in both of those cases.
   *
   * Phase 2A: inside a genuine BPF popup/window (mode=partner-window),
   * the label reads "Save Memorial & Continue to [Partner]" — the action
   * now does more than navigate (it saves a durable draft AND hands off
   * to the widget via postMessage). The legacy "Return to [Partner] to
   * Purchase" copy is kept for the direct-link path (no mode param),
   * matching handleReturnToPartner()'s own two-branch behavior above.
   */
  function decoratePartnerButton(buttonEl, labelEl) {
    if (!buttonEl || !labelEl) return;
    resolveContext().then(function (ctx) {
      if (!ctx.valid) return;
      buttonEl.classList.add('bpf-partner-cta');
      labelEl.textContent = isPartnerWindowMode()
        ? 'Save Memorial & Continue to ' + ctx.displayName
        : 'Return to ' + ctx.displayName + ' to Purchase';
    });
  }

  // --- Phase 2A: BPF popup/window presentation mode ---

  /**
   * Sends a versioned BPF_DRAFT_SAVED message to this window's opener
   * (the partner-page widget) and then attempts to close this window.
   * targetOrigin comes from `parent_origin` (supplied by the widget when
   * IT opened this window, since only it knows its own origin) — the
   * browser independently verifies the opener's actual origin matches
   * before delivering the message, so this is never a trust decision on
   * BPF's side, only a delivery-address hint. Falls back to an inline
   * "you can close this window" state if window.close() doesn't take
   * effect (some browsers refuse to close windows not opened by script,
   * or a user-resized/refocused popup) rather than leaving the customer
   * on a dead-end screen with no explanation.
   */
  function notifyOpenerDraftSaved(partnerDraftId) {
    postToOpener({
      type: 'BPF_DRAFT_SAVED',
      version: PARTNER_WINDOW_MESSAGE_VERSION,
      partnerId: getPartnerId(),
      partnerDraftId: partnerDraftId,
    });
    attemptWindowClose();
  }

  /**
   * NEVER falls back to '*' — a wildcard targetOrigin would deliver the
   * message to window.opener regardless of its actual origin, exactly
   * the "arbitrary target origin" this architecture must not risk. In
   * practice this function is only ever reached after initPartnerWindowPresentation()/
   * handleReturnToPartner() have already confirmed ctx.valid via
   * resolvePartnerWindowContext() on the backend, which itself requires
   * a non-empty, approved rawParentOrigin to have succeeded — so
   * rawParentOrigin is guaranteed present by the time this runs. The
   * explicit guard below is defense-in-depth, not the primary control:
   * if that invariant is ever violated by a future change, this fails
   * closed (no message sent) rather than silently widening to '*'.
   */
  function postToOpener(message) {
    try {
      if (!window.opener || window.opener.closed) return false;
      if (!rawParentOrigin) return false;
      window.opener.postMessage(message, rawParentOrigin);
      debugLog('posted message to opener', { message: message, targetOrigin: rawParentOrigin });
      return true;
    } catch (e) {
      debugLog('postMessage to opener failed', { message: e && e.message });
      return false;
    }
  }

  function attemptWindowClose() {
    try {
      window.close();
    } catch (e) {
      // ignored — fallback below covers this
    }
    window.setTimeout(function () {
      if (window.closed) return;
      showManualCloseState();
    }, 400);
  }

  function showManualCloseState() {
    var chrome = document.querySelector('[data-bpf-partner-window-chrome]');
    if (!chrome) return;
    var statusEl = chrome.querySelector('[data-bpf-partner-window-chrome-status]');
    if (!statusEl) return;
    var displayName = getDisplayName() || 'the partner site';
    statusEl.textContent = 'You can close this window and return to ' + displayName + '.';
    statusEl.hidden = false;
  }

  /**
   * Renders the lightweight BPF branding + close ("X") chrome bar that
   * every page shown INSIDE a genuine BPF partner-window (the landing
   * page, the breed collection, Commerce V2) shares — built once here,
   * in JS, rather than duplicated as Liquid markup across three
   * different templates. Injected ONLY after a genuinely confirmed
   * partner context AND mode=partner-window are both true (see
   * initPartnerWindowPresentation) — never on normal BPF traffic, never
   * on a bare `?partner=` visit with no window signal.
   */
  function injectWindowChrome(ctx) {
    if (document.querySelector('[data-bpf-partner-window-chrome]')) return; // idempotent across repeated init calls
    var chrome = document.createElement('div');
    chrome.className = 'bpf-partner-window-chrome';
    chrome.setAttribute('data-bpf-partner-window-chrome', '');

    var brand = document.createElement('span');
    brand.className = 'bpf-partner-window-chrome__brand';
    brand.textContent = 'Best Pets Forever';
    chrome.appendChild(brand);

    var status = document.createElement('span');
    status.className = 'bpf-partner-window-chrome__status';
    status.setAttribute('data-bpf-partner-window-chrome-status', '');
    status.hidden = true;
    chrome.appendChild(status);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'bpf-partner-window-chrome__close';
    closeBtn.setAttribute('aria-label', 'Close and return to ' + (ctx.displayName || 'partner site'));
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', function () {
      dispatchPartnerEvent('partner_window_closed', { partner_id: ctx.partnerId });
      postToOpener({ type: 'BPF_WINDOW_CLOSED', version: PARTNER_WINDOW_MESSAGE_VERSION, partnerId: ctx.partnerId });
      attemptWindowClose();
    });
    chrome.appendChild(closeBtn);

    document.body.insertBefore(chrome, document.body.firstChild);
    document.body.classList.add('bpf-partner-window-chrome-active');
  }

  /**
   * Call once per page (landing, breed collection, Commerce V2). Applies
   * the Phase 2A "partner-window presentation mode" — BPF-shell
   * suppression (reusing the SAME body class/CSS selector list the
   * Partner-only commerce/promotion cleanup pass already built and
   * tested) plus the branding/close chrome — but ONLY when BOTH a
   * genuinely resolved valid partner context AND mode=partner-window are
   * true — and, as of the origin-allowlist addition, "valid" itself now
   * ALSO requires rawParentOrigin to be on that specific partner's
   * approved_embed_origins list (resolvePartnerWindowContext() on the
   * backend). A fake/unknown partner, or a forged/mismatched/missing
   * parent_origin, all fail closed the exact same way: ctx.valid is
   * simply false, so this whole function is a no-op.
   */
  // Phase 2B.1 — the earliest possible signal (layout/theme.liquid's own
  // tiny inline <head> script, presentation-only, never authorization —
  // see that file's own comment) may have already added a PENDING class
  // to <html> the instant this page loaded, purely from the raw
  // (untrusted) `mode=partner-window` URL value. This function is the
  // ONLY place that ever promotes that into (or clears it back out of) a
  // real state, and only after resolveContext()'s async backend check —
  // partner/windowMode/parent_origin/approved-origin — has actually
  // resolved. Removed unconditionally on BOTH outcomes below: on success
  // the real, validated `body.bpf-partner-commerce-suppressed` class
  // (already covering the identical selector list) takes over, making
  // the pending class redundant; on failure the normal site must be
  // fully restored, safely, with no lingering suppression of any kind.
  function clearPendingPresentation() {
    document.documentElement.classList.remove('bpf-partner-window-pending');
  }

  function initPartnerWindowPresentation() {
    if (!isPartnerWindowMode()) return Promise.resolve({ activated: false });
    return resolveContext().then(function (ctx) {
      if (!ctx.valid) {
        // Phase 2B.2A — reaching this specific branch means the page
        // genuinely carried mode=partner-window (a real popup
        // construction, not random bot/probe traffic hitting a bare
        // ?partner=), yet context resolution still came back invalid —
        // a meaningfully different, worth-recording signal from the
        // generic "unknown partner" case resolveContext() alone can't
        // distinguish from noise.
        sendPartnerTelemetry('partner_context_error', { partnerId: rawPartnerKey, partnerSessionId: rawPartnerSessionId, errorCode: 'context_invalid', stage: 'partner_window_presentation' });
        clearPendingPresentation();
        return { activated: false };
      }
      document.body.classList.add('bpf-partner-commerce-suppressed');
      injectWindowChrome(ctx);
      clearPendingPresentation();
      return { activated: true };
    });
  }

  /**
   * For BPF-owned partner-context pages (the partner landing page, and
   * historically the Phase 1 partner-preview page): appends the full set
   * of PROPAGATED_PARAMS this page itself was opened with (partner, and,
   * when present, mode + parent_origin) onto every outbound breed/animal
   * navigation link inside `containerEl`, so partner-window context
   * survives real top-level navigation into the breed collection page as
   * ordinary URL parameters — never via localStorage/sessionStorage as
   * the source of truth for the next page (those remain convenience-only
   * caches elsewhere in this module). Only ever copies a param that was
   * actually present on THIS page — never invents mode/parent_origin for
   * a plain `?partner=`-only visit.
   */
  function rewriteLinks(containerEl) {
    if (!containerEl || !rawPartnerKey) return;
    resolveContext().then(function (ctx) {
      if (!ctx.valid) return;
      var currentParams;
      try {
        currentParams = new URLSearchParams(window.location.search);
      } catch (e) {
        currentParams = null;
      }
      var links = containerEl.querySelectorAll('a[href]');
      links.forEach(function (link) {
        var href = link.getAttribute('href');
        if (!href || href.charAt(0) === '#') return;
        try {
          var url = new URL(href, window.location.origin);
          PROPAGATED_PARAMS.forEach(function (key) {
            var value = key === 'partner' ? rawPartnerKey : currentParams && currentParams.get(key);
            if (value) url.searchParams.set(key, value);
          });
          link.setAttribute('href', url.pathname + url.search + url.hash);
        } catch (e) {
          // Malformed/unparseable href — leave it untouched rather than risk breaking navigation.
        }
      });
    });
  }

  window.BPFPartnerMode = {
    resolveContext: resolveContext,
    isActive: isActive,
    getDisplayName: getDisplayName,
    getPartnerId: getPartnerId,
    handleReturnToPartner: handleReturnToPartner,
    attachToForm: attachToForm,
    decoratePartnerButton: decoratePartnerButton,
    rewriteLinks: rewriteLinks,
    dispatchPartnerEvent: dispatchPartnerEvent,
    hasPartnerParam: Boolean(rawPartnerKey),
    getMode: getMode,
    isPartnerWindowMode: isPartnerWindowMode,
    initPartnerWindowPresentation: initPartnerWindowPresentation,
    // Phase 2B.2A — exposed so sibling partner-context pages (the
    // landing page, Commerce V2) can send an operational-telemetry
    // event through the SAME sender/allowlist/session-id this file
    // already owns, rather than each maintaining its own duplicate copy
    // (unlike the core widget/adapter, this file is never distributed
    // externally, so sharing via its own already-public API surface —
    // the same pattern dispatchPartnerEvent/getPartnerId already use —
    // is the right call here).
    sendPartnerTelemetry: sendPartnerTelemetry,
    getPartnerSessionId: function () {
      return rawPartnerSessionId;
    },
  };

  // Kick off resolution immediately (module load time) ONLY when a
  // partner param is actually present — zero network activity, zero
  // behavior change for the overwhelming majority of normal BPF traffic
  // that never carries this parameter at all.
  if (rawPartnerKey) {
    resolveContext();
  }
})();
